import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateAdminPlacementTestTemplate } from '../../../shared/api/placement-tests.services';
import toast from '../../../shared/lib/toast';

// Мини-тест — это опросник из трёх вопросов (для кого, ціль, рівень).
// Он не оценивается: ни правильных ответов, ни баллов, ни уровней CEFR.
// Поэтому форма редактирует только тексты, а служебные поля шаблона
// (intro, settings, cefrScale) не трогает — сервер сохраняет их как есть.
const validationSchema = Yup.object({
  title: Yup.object({
    ua: Yup.string().required('Назва UA необхідна'),
    en: Yup.string().required('Назва EN необхідна'),
  }),
  slug: Yup.string().required('Slug необхідний'),
  questionPool: Yup.array()
    .of(
      Yup.object({
        prompt: Yup.object({
          ua: Yup.string().required('Текст питання UA необхідний'),
          en: Yup.string().required('Текст питання EN необхідний'),
        }),
        options: Yup.array()
          .of(
            Yup.object({
              text: Yup.object({
                ua: Yup.string().required('Текст варіанту UA необхідний'),
                en: Yup.string().required('Текст варіанту EN необхідний'),
              }),
            })
          )
          .min(2, 'У питанні має бути щонайменше два варіанти'),
      })
    )
    .min(1, 'Потрібне щонайменше одне питання'),
});

const normalizeToLocalized = (val) => {
  if (typeof val === 'string') {
    return { ua: val, en: val };
  }
  if (typeof val === 'object' && val !== null && (val.ua !== undefined || val.en !== undefined)) {
    return { ua: val.ua || '', en: val.en || '' };
  }
  return { ua: '', en: '' };
};

const normalizeString = (val) => {
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null) {
    return val.ua || val.en || '';
  }
  return '';
};

// В шаблоне варианты лежат внутри groups. Мини-тест всегда использует ровно
// одну группу (клиент читает groups[0].options), поэтому в форме показываем
// плоский список вариантов, а при сохранении заворачиваем обратно.
const buildInitialValues = (template) => {
  if (!template) {
    return {
      title: { ua: '', en: '' },
      slug: '',
      isActive: true,
      questionPool: [],
    };
  }

  return {
    title: normalizeToLocalized(template.title),
    slug: normalizeString(template.slug),
    isActive: template.isActive !== false,
    questionPool: (Array.isArray(template.questionPool) ? template.questionPool : []).map((question) => ({
      id: normalizeString(question.id),
      groupId: normalizeString(question.groups?.[0]?.id),
      prompt: normalizeToLocalized(question.prompt),
      options: (question.groups?.[0]?.options || []).map((option) => ({
        id: normalizeString(option.id),
        label: normalizeString(option.label),
        text: normalizeToLocalized(option.text),
      })),
    })),
  };
};

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const buildQuestionPoolPayload = (questions) =>
  questions.map((question, questionIndex) => {
    const questionId = question.id || `question-${questionIndex + 1}`;

    return {
      id: questionId,
      type: 'single_choice_group',
      prompt: question.prompt,
      helperText: null,
      groups: [
        {
          id: question.groupId || `${questionId}-group`,
          label: '1',
          options: (question.options || []).map((option, optionIndex) => ({
            id: option.id || `${questionId}-option-${optionIndex + 1}`,
            label: option.label || OPTION_LABELS[optionIndex] || String(optionIndex + 1),
            text: option.text,
          })),
        },
      ],
    };
  });

const useQuickPlacementTestForm = (initialData, templateId) => {
  const formik = useFormik({
    initialValues: buildInitialValues(initialData),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const questionPool = buildQuestionPoolPayload(values.questionPool);
        const payload = {
          title: values.title,
          slug: values.slug?.trim() || 'quick-placement-test-ubc',
          isActive: values.isActive,
          // Помечаем шаблон как информационный: сервер не требует отметку
          // «правильний варіант» и не показывает оценку в сессиях.
          isInformational: true,
          // Настройки в форме не показываем, но держим согласованными с пулом:
          // сайт выводит опросник целиком одним куском, а questionCount меньше
          // числа вопросов ломает сохранение на валидации сервера.
          settings: {
            questionCount: questionPool.length,
            inlineQuestions: true,
            randomizeQuestions: false,
            randomizeOptions: false,
          },
          questionPool,
        };

        const response = await updateAdminPlacementTestTemplate(templateId, payload);

        if (response?.error) {
          toast.error(response.message || 'Помилка при збереженні');
          return;
        }

        toast.success('Міні-тест успішно оновлено!');
      } catch (error) {
        console.error('Error updating template:', error);
        toast.error('Помилка при збереженні шаблону');
      }
    },
  });

  return formik;
};

export { useQuickPlacementTestForm };
